import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as rxjs from 'rxjs'

import * as common from '../../common'

import * as gql from './gql'
import * as models from './models'

@ngCore.Injectable({ providedIn: 'root' })
export class Api {
  /**
   * Returns a list of {@link models.User} objects filtered by
   * {@link models.User.Filter}.
   * Primarily used by the assignment feature to fetch users eligible for
   * assignment in a specific entity.
   */
  public listUsers(
    filters?: models.User.Filters,
  ): rxjs.Observable<readonly models.User[]> {
    if (!filters?.context) {
      return gql.queries.users
        .execute()
        .pipe(rxjs.map(response => response.items))
    }

    const contextUsers$ = this._listContextUsers(filters.context)

    return filters.includePlatformUsers
      ? rxjs
          .forkJoin([
            contextUsers$,
            gql.queries.users
              .execute()
              .pipe(rxjs.map(response => response.items)),
          ])
          .pipe(
            rxjs.map(([contextUsers, platformUsers]) =>
              models.User.deduplicate([...contextUsers, ...platformUsers]),
            ),
          )
      : contextUsers$
  }

  private _listContextUsers(
    context:
      | ents.core.admin.Context.ForTenant
      | ents.core.admin.Context.ForZone
      | ents.core.admin.Context.ForApp,
  ): rxjs.Observable<readonly models.User[]> {
    const appId = 'appId' in context ? context.appId : undefined
    const tenantId = context.tenantId
    const zoneId = 'zoneId' in context ? context.zoneId : undefined

    return (
      appId && zoneId && tenantId
        ? gql.queries.inApp.execute({
            appId,
            tenantId,
            zoneId,
          })
        : zoneId && tenantId
          ? gql.queries.inZone.execute({
              tenantId,
              zoneId,
            })
          : tenantId
            ? gql.queries.inTenant.execute({
                tenantId,
              })
            : gql.queries.users.execute()
    ).pipe(rxjs.map(response => response.items))
  }

  /**
   * Manages the assignment of user access {@link ents.core.admin.Access} to a
   * selected entity (tenant, zone, or application)
   */
  public assignAccess(
    assignments: readonly ents.core.admin.Access.Assignment[],
  ): rxjs.Observable<boolean | undefined> {
    return gql.mutations.assignAccess.executor({
      assignments: assignments.map(assignment => ({
        ...assignment,
        agentId: assignment.agentId
          ? common.gql.enums.agentId.TRANSFORMER.getGqlFrom(assignment.agentId)
          : undefined,
        excludePages: assignment.excludePages
          ? Array.from(assignment.excludePages)
          : undefined,
        includePages: assignment.includePages
          ? Array.from(assignment.includePages)
          : undefined,
      })),
    })
  }
}
